import { useState, useEffect, useRef, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { UtensilsCrossed, Plus, X, Users, Check, Search, AlertTriangle, CalendarCheck } from 'lucide-react';

// Sprint 74 (1/N) — mapa de mesas: cada mesa es un AgendaResource
// (resource_type='table') con seat_capacity + pos_x/pos_y guardados en un
// canvas relativo de 0-1000 x 0-600. Arrastrar una mesa cerca de otra
// (< 160 unidades, ver ADJACENCY_THRESHOLD en restaurant_tables.py) hace
// que el motor de reservaciones las pueda combinar para grupos grandes.
const CANVAS_W = 1000;
const CANVAS_H = 600;
const SHAPES = [
  { key: 'square', label: 'Cuadrada' },
  { key: 'round', label: 'Redonda' },
  { key: 'rect', label: 'Rectangular' },
];

function TableNode({ table, onDrag, onDragEnd, onClick, selected }) {
  const ref = useRef(null);
  const dragging = useRef(false);

  const handlePointerDown = (e) => {
    dragging.current = true;
    ref.current?.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (!dragging.current) return;
    const canvas = ref.current?.parentElement;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(CANVAS_W, ((e.clientX - rect.left) / rect.width) * CANVAS_W));
    const y = Math.max(0, Math.min(CANVAS_H, ((e.clientY - rect.top) / rect.height) * CANVAS_H));
    onDrag(table.id, x, y);
  };
  const handlePointerUp = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    ref.current?.releasePointerCapture(e.pointerId);
    onDragEnd(table);
  };

  const isRound = table.shape === 'round';
  const isRect = table.shape === 'rect';

  return (
    <div
      ref={ref}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={() => onClick(table)}
      data-testid={`table-node-${table.id}`}
      className={`absolute flex flex-col items-center justify-center cursor-grab active:cursor-grabbing select-none border-2 shadow-sm ${
        isRound ? 'rounded-full' : isRect ? 'rounded-md' : 'rounded-lg'
      } ${table.is_active ? 'bg-white' : 'bg-gray-100 opacity-60'} ${
        selected ? 'border-[#0F172A] ring-2 ring-[#0F172A]/20' : 'border-[#CBD5E1]'
      }`}
      style={{
        left: `${((table.pos_x ?? 100) / CANVAS_W) * 100}%`,
        top: `${((table.pos_y ?? 100) / CANVAS_H) * 100}%`,
        width: isRect ? 90 : 64,
        height: isRect ? 56 : 64,
        transform: 'translate(-50%, -50%)',
      }}
    >
      <span className="text-[11px] font-semibold text-[#0F172A] truncate max-w-[80%]">{table.name}</span>
      <span className="text-[10px] text-[#64748B] flex items-center gap-0.5">
        <Users className="w-2.5 h-2.5" /> {table.seat_capacity ?? '—'}
      </span>
    </div>
  );
}

export default function RestaurantFloorPlan() {
  const [tables, setTables] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newTable, setNewTable] = useState({ name: '', seat_capacity: 4, shape: 'square', location_id: '', buffer_after_minutes: 15 });
  const [saving, setSaving] = useState(false);
  const dragBuffer = useRef({});

  // Verificador de disponibilidad manual — mismo motor que usa la IA
  // (find_table_combo), para que un anfitrión pueda probar "¿me cabe un
  // grupo de 20?" antes de confirmar por teléfono.
  const [checkForm, setCheckForm] = useState({ starts_at: '', duration_minutes: 90, party_size: 4 });
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);

  // S76 (10/N) — confirmar la reservación desde aquí mismo. El backend
  // re-ejecuta find_table_combo del lado del servidor (nunca confía en las
  // mesas que eligió el cliente), así que el anfitrión usa exactamente el
  // mismo motor de combinación de mesas que la IA de WhatsApp/llamadas.
  const [leads, setLeads] = useState([]);
  const [reserveForm, setReserveForm] = useState({ crm_lead_id: '', notes: '' });
  const [reserving, setReserving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, locRes] = await Promise.all([
        api.get('/tenant/agenda/resources'),
        api.get('/tenant/locations').catch(() => ({ data: [] })),
      ]);
      const all = resRes.data?.items || [];
      setTables(all.filter(r => r.resource_type === 'table'));
      setLocations(locRes.data?.items || locRes.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDrag = (id, x, y) => {
    dragBuffer.current[id] = { x, y };
    setTables(ts => ts.map(t => (t.id === id ? { ...t, pos_x: x, pos_y: y } : t)));
  };

  const handleDragEnd = async (table) => {
    const buffered = dragBuffer.current[table.id];
    if (!buffered) return;
    delete dragBuffer.current[table.id];
    try {
      await api.put(`/tenant/agenda/resources/${table.id}`, { pos_x: buffered.x, pos_y: buffered.y });
    } catch (e) { toast.error('No se pudo guardar la posición de la mesa'); }
  };

  const handleCreateTable = async () => {
    if (!newTable.name.trim()) { toast.error('Ponle un nombre a la mesa (ej. "Mesa 1", "Terraza A")'); return; }
    if (!newTable.seat_capacity || newTable.seat_capacity < 1) { toast.error('La capacidad debe ser al menos 1 comensal'); return; }
    setSaving(true);
    try {
      await api.post('/tenant/agenda/resources', {
        resource_type: 'table',
        name: newTable.name.trim(),
        seat_capacity: newTable.seat_capacity,
        buffer_after_minutes: Math.max(0, Number(newTable.buffer_after_minutes) || 0),
        shape: newTable.shape,
        location_id: newTable.location_id || undefined,
        pos_x: 80 + (tables.length % 8) * 110,
        pos_y: 80 + Math.floor(tables.length / 8) * 110,
      });
      toast.success('Mesa agregada al mapa');
      setNewTable({ name: '', seat_capacity: 4, shape: 'square', location_id: '', buffer_after_minutes: 15 });
      setShowNew(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo crear la mesa'); }
    finally { setSaving(false); }
  };

  const handleUpdateSelected = async (patch) => {
    if (!selected) return;
    try {
      await api.put(`/tenant/agenda/resources/${selected.id}`, patch);
      setSelected(s => ({ ...s, ...patch }));
      setTables(ts => ts.map(t => (t.id === selected.id ? { ...t, ...patch } : t)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo actualizar la mesa'); }
  };

  const handleToggleActive = async () => {
    if (!selected) return;
    await handleUpdateSelected({ is_active: !selected.is_active });
    toast.success(selected.is_active ? 'Mesa desactivada' : 'Mesa activada');
  };

  const handleCheckAvailability = async () => {
    if (!checkForm.starts_at) { toast.error('Elige fecha y hora'); return; }
    setChecking(true);
    setCheckResult(null);
    try {
      const { data } = await api.get('/tenant/agenda/tables/availability', {
        params: {
          starts_at: new Date(checkForm.starts_at).toISOString(),
          duration_minutes: checkForm.duration_minutes,
          party_size: checkForm.party_size,
        },
      });
      setCheckResult(data);
      if (data?.ok && leads.length === 0) {
        try {
          const lr = await api.get('/tenant/crm/leads?status=active&page_size=50');
          setLeads(lr.data?.items || lr.data || []);
        } catch { /* el selector mostrará el aviso de "sin clientes" */ }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al verificar disponibilidad'); }
    finally { setChecking(false); }
  };

  const handleConfirmReservation = async () => {
    if (!reserveForm.crm_lead_id) { toast.error('Elige a nombre de quién es la reservación'); return; }
    setReserving(true);
    try {
      const { data } = await api.post('/tenant/agenda/tables/reservations', {
        crm_lead_id: reserveForm.crm_lead_id,
        starts_at: new Date(checkForm.starts_at).toISOString(),
        duration_minutes: checkForm.duration_minutes,
        party_size: checkForm.party_size,
        notes: reserveForm.notes || undefined,
      });
      const mesas = (data.tables || []).map(t => t.name).join(' + ');
      toast.success(`Reservación creada${mesas ? ` — mesas: ${mesas}` : ''}`);
      setCheckResult(null);
      setReserveForm({ crm_lead_id: '', notes: '' });
    } catch (e) {
      const detail = e.response?.data?.detail;
      // El 409 del motor trae {error, message} — mostramos su mensaje en el
      // mismo bloque ámbar del verificador, porque significa que la
      // disponibilidad cambió entre verificar y confirmar.
      if (e.response?.status === 409 && detail && typeof detail === 'object' && detail.message) {
        setCheckResult({ ok: false, reason: detail.error, message: detail.message });
      } else {
        toast.error(formatApiError(detail) || 'No se pudo crear la reservación');
      }
    } finally { setReserving(false); }
  };

  const totalCapacity = tables.filter(t => t.is_active).reduce((sum, t) => sum + (t.seat_capacity || 0), 0);

  return (
    <div className="space-y-4" data-testid="restaurant-floor-plan">
      <div>
        <h2 className="text-sm font-bold flex items-center gap-2">
          <UtensilsCrossed className="w-4 h-4" /> Mapa de mesas
          <HelpTooltip text="Coloca tus mesas como están físicamente en tu restaurante. Cuando alguien reserve para un grupo grande, el sistema junta mesas que estén cerca en este mapa — nunca inventa una mesa que no exista." />
        </h2>
        <p className="text-xs text-[#64748B] mt-0.5">
          Arrastra cada mesa a su posición real. Las mesas que quedan cerca entre sí en este mapa se pueden combinar
          automáticamente para grupos grandes (ej. una reservación de 20 personas que requiera juntar 3-4 mesas).
          Si ningún arreglo de mesas cubre al grupo, tu IA de WhatsApp/llamadas nunca inventa una — te avisa para que
          un anfitrión atienda directamente.
        </p>
      </div>

      <SectionHelp
        module="restaurant_floor_plan"
        title="Mapa de mesas"
        description="Layout visual de tu restaurante: posición y capacidad de cada mesa, usado por el motor de reservaciones (manual, WhatsApp y llamadas con IA)."
        whatItDoesNot="No reemplaza tu sistema de comandas/POS — solo controla a qué mesa se asigna cada reservación."
      />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-[#64748B]">
          {tables.length} mesa{tables.length !== 1 ? 's' : ''} · capacidad total activa {totalCapacity} comensales
        </p>
        <Button size="sm" onClick={() => setShowNew(!showNew)} data-testid="floorplan-new-table-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva mesa
        </Button>
      </div>

      {showNew && (
        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-[#64748B]">Nombre</label>
              <Input placeholder="Mesa 1" value={newTable.name}
                onChange={e => setNewTable({ ...newTable, name: e.target.value })}
                data-testid="floorplan-new-name" />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Capacidad (comensales)</label>
              <Input type="number" min={1} value={newTable.seat_capacity}
                onChange={e => setNewTable({ ...newTable, seat_capacity: Math.max(1, +e.target.value) })}
                data-testid="floorplan-new-capacity" />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Tiempo para limpiar la mesa (minutos)</label>
              <Input type="number" min={0} max={480} step={5} value={newTable.buffer_after_minutes}
                onChange={e => setNewTable({ ...newTable, buffer_after_minutes: Math.max(0, +e.target.value) })}
                data-testid="floorplan-new-buffer" />
              <p className="text-[11px] text-[#94A3B8] mt-0.5">
                Lo que tardan en levantar, limpiar y volver a poner el servicio. Con esto,
                la mesa deja de ofrecerse pegada a la reservación anterior. 0 = sin espera.
              </p>
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Forma</label>
              <select value={newTable.shape} onChange={e => setNewTable({ ...newTable, shape: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="floorplan-new-shape">
                {SHAPES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            {locations.length > 0 && (
              <div className="sm:col-span-3">
                <label className="text-xs text-[#64748B]">Sede (opcional)</label>
                <select value={newTable.location_id} onChange={e => setNewTable({ ...newTable, location_id: e.target.value })}
                  className="w-full border rounded px-2 py-1.5 text-sm">
                  <option value="">Todas las sedes</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button size="sm" onClick={handleCreateTable} disabled={saving} data-testid="floorplan-new-save">
              {saving ? 'Guardando...' : 'Agregar mesa'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-[#94A3B8] text-center py-10">Cargando...</p>
      ) : tables.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center">
          <UtensilsCrossed className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Todavía no tienes mesas en el mapa. Agrega la primera arriba.</p>
        </div>
      ) : (
        <div
          className="relative w-full bg-[#F8FAFC] border-2 border-dashed border-[#E2E8F0] rounded-xl overflow-hidden"
          style={{ aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}
          data-testid="floorplan-canvas"
        >
          {tables.map(t => (
            <TableNode key={t.id} table={t} onDrag={handleDrag} onDragEnd={handleDragEnd}
              onClick={setSelected} selected={selected?.id === t.id} />
          ))}
        </div>
      )}

      {selected && (
        <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="floorplan-selected-panel">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold">{selected.name}</h3>
            <button onClick={() => setSelected(null)}><X className="w-4 h-4 text-[#94A3B8]" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-[#64748B]">Nombre</label>
              <Input value={selected.name} onChange={e => setSelected(s => ({ ...s, name: e.target.value }))}
                onBlur={() => handleUpdateSelected({ name: selected.name })} />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Capacidad</label>
              <Input type="number" min={1} value={selected.seat_capacity ?? ''}
                onChange={e => setSelected(s => ({ ...s, seat_capacity: +e.target.value }))}
                onBlur={() => handleUpdateSelected({ seat_capacity: selected.seat_capacity })} />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Forma</label>
              <select value={selected.shape || 'square'}
                onChange={e => handleUpdateSelected({ shape: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm">
                {SHAPES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={handleToggleActive} data-testid="floorplan-toggle-active">
              {selected.is_active ? (<><X className="w-3.5 h-3.5 mr-1" /> Desactivar mesa</>) : (<><Check className="w-3.5 h-3.5 mr-1" /> Activar mesa</>)}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <h3 className="text-sm font-bold flex items-center gap-1.5">
          <Search className="w-4 h-4" /> Verificar disponibilidad
          <HelpTooltip text="Simula la misma búsqueda que usa la IA: prueba si una fecha/hora/número de comensales tiene mesa o combinación de mesas disponible." />
        </h3>
        <div className="grid sm:grid-cols-4 gap-2">
          <div>
            <label className="text-xs text-[#64748B]">Fecha y hora</label>
            <Input type="datetime-local" value={checkForm.starts_at}
              onChange={e => setCheckForm({ ...checkForm, starts_at: e.target.value })}
              data-testid="floorplan-check-datetime" />
          </div>
          <div>
            <label className="text-xs text-[#64748B]">Duración (min)</label>
            <Input type="number" min={30} step={30} value={checkForm.duration_minutes}
              onChange={e => setCheckForm({ ...checkForm, duration_minutes: +e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[#64748B]">Comensales</label>
            <Input type="number" min={1} value={checkForm.party_size}
              onChange={e => setCheckForm({ ...checkForm, party_size: +e.target.value })}
              data-testid="floorplan-check-party-size" />
          </div>
          <div className="flex items-end">
            <Button size="sm" onClick={handleCheckAvailability} disabled={checking} className="w-full" data-testid="floorplan-check-btn">
              {checking ? 'Buscando...' : 'Verificar'}
            </Button>
          </div>
        </div>
        {checkResult && (
          checkResult.ok ? (
            <div className="space-y-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="floorplan-check-ok">
                Hay disponibilidad: {checkResult.tables.map(t => `${t.name} (${t.seat_capacity})`).join(' + ')}
                {' '}— capacidad total {checkResult.total_capacity} comensales.
              </div>
              <div className="bg-white border rounded-lg p-3 space-y-2" data-testid="floorplan-reserve-form">
                <p className="text-xs font-semibold flex items-center gap-1.5">
                  <CalendarCheck className="w-3.5 h-3.5" /> Confirmar reservación
                  <HelpTooltip text="Crea la reservación con estas mesas usando el mismo motor que la IA — si alguien más ganó el horario mientras tanto, te avisamos en lugar de duplicar la mesa." />
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[#64748B]">A nombre de</label>
                    <select value={reserveForm.crm_lead_id}
                      onChange={e => setReserveForm({ ...reserveForm, crm_lead_id: e.target.value })}
                      className="w-full border rounded px-2 py-1.5 text-sm" data-testid="floorplan-reserve-lead">
                      <option value="">Elige un cliente...</option>
                      {leads.map(l => <option key={l.id} value={l.id}>{l.display_name}</option>)}
                    </select>
                    {leads.length === 0 && (
                      <p className="text-[11px] text-[#94A3B8] mt-0.5">No hay clientes activos en el CRM — crea primero el lead en la sección CRM.</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B]">Notas (opcional)</label>
                    <Input placeholder="Cumpleaños, silla para bebé..." value={reserveForm.notes}
                      onChange={e => setReserveForm({ ...reserveForm, notes: e.target.value })}
                      data-testid="floorplan-reserve-notes" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={handleConfirmReservation} disabled={reserving} data-testid="floorplan-reserve-confirm">
                    {reserving ? 'Reservando...' : 'Confirmar reservación'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="floorplan-check-fail">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              {checkResult.message
                ? checkResult.message
                : checkResult.reason === 'no_tables_available'
                  ? 'Ninguna mesa está libre en ese horario.'
                  : checkResult.reason === 'capacity_exceeded'
                    ? 'Ninguna mesa (ni combinación de mesas cercanas en el mapa) cubre a este grupo — atiéndelo directamente.'
                    : 'No se pudo verificar la disponibilidad.'}
            </div>
          )
        )}
      </div>
    </div>
  );
}
